import React from "react";
import { useTranslation } from "react-i18next";
import { Typography } from "@mui/material";
import s from "./StatusWrapper.module.scss";

const StatusWrapper = ({
  variant,
  size = "medium",
  status,
  icon,
  children,
  className = "",
  shape = "pill",
  clickable = false,
  onClick,
  style = {},
  testId,
  ...props
}) => {
  const { t } = useTranslation();

  const resolvedVariant = variant ?? getStatusVariant(status);

  const classNames = [
    s["status-wrapper"],
    s[`variant-${resolvedVariant}`],
    s[`size-${size}`],
    s[`shape-${shape}`],
    className,
    icon && s["icon-container"]
  ].filter(Boolean).join(" ");

  const getContent = () => {
    if (children) {
      return children;
    }
    
    if (status) {
      const translatedStatus = t(status);
      const displayText = translatedStatus !== status ? translatedStatus : status.replace(/_/g, " ");
      
      return (
        <Typography
          component="span"
          sx={{
            fontFamily: "inherit",
            fontSize: "inherit",
            fontWeight: "inherit",
            lineHeight: "inherit",
            letterSpacing: "inherit",
          }}
        >
          {displayText}
        </Typography>
      );
    }
    
    return null;
  };

  const handleClick = (e) => {
    if (clickable && onClick) {
      onClick(e);
    }
  };

  return (
    <div
      className={classNames}
      style={style}
      data-testid={testId}
      {...props}
    >
      {getContent()}
      {icon && <div className={s["icon-container"]} onClick={handleClick} style={{ cursor: clickable ? "pointer" : "default" }}>{icon}</div>}
    </div>
  );
};

const getStatusVariant = (status) => {
  if (!status) return 'grey';

  const statusLower = status.toLowerCase();

  // Success states
  if (['success', 'approved', 'active', 'settled', 'authorized', 'onboarded'].includes(statusLower)) {
    return 'green';
  }

  // Pending states (grey)
  if (['pending', 'waiting'].includes(statusLower)) {
    return 'grey';
  }

  // Warning states  
  if (['suspended', 'voided', 'reversed', 'partially_refunded', 'refunded', 'offboarded', 'update_failed'].includes(statusLower)) {
    return 'orange';
  }

  // Error states
  if (['declined', 'failed', 'rejected', 'cancelled', 'timeout', 'terminated', 'onboard_failed', 'offboard_failed'].includes(statusLower)) {
    return 'red';
  }

  // Info states
  if (['open', 'processing'].includes(statusLower)) {
    return 'blue';
  }

  // Neutral states
  if (['closed', 'none', 'inactive', 'not_onboarded'].includes(statusLower)) {
    return 'grey';
  }

  return 'grey';
};

export default StatusWrapper;
